import React, { useState, useEffect, useRef } from "react";
import { FieldError, UseFormRegister, UseFormSetValue } from "react-hook-form";
import axios from "axios";

const MAPBOX_TOKEN = process.env.REACT_APP_MAP_API;

interface MyInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  onSelect: (location: any) => void;
  name: string;
  setValue?: UseFormSetValue<any>;
  register: UseFormRegister<any>;
  error?: FieldError;
  validationRules?: object;
}

function LocationAutoComplete({
  onSelect,
  name,
  register,
  error,
  validationRules,
  ...rest
}: MyInputProps) {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const selectedRef = useRef(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const {
    ref,
    onChange: hookFormOnChange,
    onBlur,
    name: inputName,
  } = register(name, validationRules);

  // 🔐 Hide suggestions on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setSuggestions([]);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // 📡 Fetch suggestions
  useEffect(() => {
    if (!query || selectedRef.current) {
      selectedRef.current = false;
      return;
    }

    const fetchSuggestions = async () => {
      try {
        const res = await axios.get(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json`,
          {
            params: {
              access_token: MAPBOX_TOKEN,
              limit: 5,
            },
          },
        );
        setSuggestions(res.data.features);
      } catch (error) {
        console.error("Failed to fetch suggestions", error);
      }
    };

    const timeout = setTimeout(fetchSuggestions, 300);
    return () => clearTimeout(timeout);
  }, [query]);

  return (
    <div className="mb-3 relative" ref={wrapperRef}>
      <input
        name={inputName}
        ref={ref}
        onBlur={onBlur}
        onChange={(e) => {
          setQuery(e.target.value);
          selectedRef.current = false;
          hookFormOnChange(e);
        }}
        value={query}
        // placeholder="Enter Location"
        autoComplete="off"
        className="h-9 w-full border px-4 py-1 text-sm shadow-theme-xs placeholder:text-gray-400 focus:outline-none focus:ring bg-transparent text-gray-800 border-gray-300 focus:border-brand-300 focus:ring focus:ring-brand-500/10"
        {...rest}
      />
      {suggestions.length > 0 && (
        <ul className="bg-white border rounded shadow mt-1 max-h-60 overflow-y-auto z-10 absolute w-full">
          {suggestions.map((place) => (
            <li
              key={place.id}
              className="p-2 hover:bg-gray-100 cursor-pointer"
              onClick={() => {
                onSelect({
                  name: place.place_name,
                  lat: place.center[1],
                  lng: place.center[0],
                });
                selectedRef.current = true;
                setQuery(place.place_name);
                setSuggestions([]);
              }}
            >
              {place.place_name}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default LocationAutoComplete;
